/**
 * One rule for "workshop or implementation". The pitch agent, the orchestrator,
 * the session state, the proposal and admin read this. Do not copy the 0.7.
 */
export const SERVICE_FIT_THRESHOLD = 0.7

export type ServiceRecommendationKind = 'workshop' | 'implementation'

export interface ServiceRecommendationV1 {
  kind: ServiceRecommendationKind
  score: number
  reason: string
}

function score(fitScore: unknown, key: 'workshop' | 'consulting'): number {
  if (!fitScore || typeof fitScore !== 'object') return 0
  const value = (fitScore as Record<string, unknown>)[key]
  return typeof value === 'number' && Number.isFinite(value) ? value : 0
}

/** Workshop wins a tie. Nothing at or under the threshold. */
export function recommendServiceFromFitScore(fitScore: unknown): ServiceRecommendationV1 | null {
  const workshop = score(fitScore, 'workshop')
  const consulting = score(fitScore, 'consulting')
  if (workshop > SERVICE_FIT_THRESHOLD && workshop >= consulting) {
    return {
      kind: 'workshop',
      score: workshop,
      reason: `Workshop fit ${workshop.toFixed(2)} is over ${SERVICE_FIT_THRESHOLD} and not under implementation fit ${consulting.toFixed(2)}.`,
    }
  }
  if (consulting > SERVICE_FIT_THRESHOLD && consulting > workshop) {
    return {
      kind: 'implementation',
      score: consulting,
      reason: `Implementation fit ${consulting.toFixed(2)} is over ${SERVICE_FIT_THRESHOLD} and over workshop fit ${workshop.toFixed(2)}.`,
    }
  }
  return null
}

export function pitchStageForRecommendation(
  fitScore: unknown,
): 'WORKSHOP_PITCH' | 'CONSULTING_PITCH' | 'PITCHING' {
  const rec = recommendServiceFromFitScore(fitScore)
  if (rec?.kind === 'workshop') return 'WORKSHOP_PITCH'
  if (rec?.kind === 'implementation') return 'CONSULTING_PITCH'
  return 'PITCHING'
}

/** Prompt line for the proposal agent. Empty when there is no recommendation. */
export function recommendationPromptLine(fitScore: unknown): string {
  const rec = recommendServiceFromFitScore(fitScore)
  if (!rec) return ''
  return rec.kind === 'workshop'
    ? 'RECOMMENDED SERVICE: workshop / course for the team first. Make the first phase a tailored workshop on their own tasks, then optional implementation.'
    : 'RECOMMENDED SERVICE: KI implementation. Make the first phase a scoped implementation sprint.'
}

export function normalizeServiceRecommendation(value: unknown): ServiceRecommendationV1 | null {
  if (!value || typeof value !== 'object') return null
  const row = value as Record<string, unknown>
  if (row.kind !== 'workshop' && row.kind !== 'implementation') return null
  if (typeof row.score !== 'number' || !Number.isFinite(row.score)) return null
  return { kind: row.kind, score: row.score, reason: typeof row.reason === 'string' ? row.reason : '' }
}
